import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { ColumnAlignment, Row, TableInstance, TableRowProps } from 'react-table';
import { NameChangeReviewGridItem } from 'shared/types';
import { useNameChangeReviewListState } from 'approval-workflow/state/name-change-review-list-state';
import { useEffect, useRef } from 'react';
import { NameChangeApprovalWorkflowTableState } from 'shared/contexts/name-change-approval-workflow-filter-context';
import { useDebounce } from 'shared/hooks';
import { NameChangeReviewTablePagination } from './name-change-review-table-pagination';

const containerStyles: SxProps<Theme> = {
  maxHeight: '40vh',
  minHeight: '100px',
};

const sortStyles: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
};

const getHeaderFlexAlignment = (align: ColumnAlignment) => {
  switch (align) {
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
};

interface NameChangeReviewTableProps {
  instance: TableInstance<NameChangeReviewGridItem>;
  setNameChangeApprovalWorkflowTableState: React.Dispatch<React.SetStateAction<NameChangeApprovalWorkflowTableState>>;
  totalCount: number;
}

export function NameChangeReviewTable({
  instance,
  totalCount,
  setNameChangeApprovalWorkflowTableState,
}: NameChangeReviewTableProps) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, state } = instance;

  const firstRender = useRef(true);
  const debouncedState = useDebounce(state, 500);

  useEffect(() => {
    const { pageIndex, pageSize, sortBy, filters, globalFilter, hiddenColumns } = debouncedState;
    if (!firstRender.current) {
      setNameChangeApprovalWorkflowTableState(prevState => {
        return {
          ...prevState,
          tableState: {
            ...prevState.tableState,
            pageIndex,
            pageSize,
            sortBy,
            filters,
            globalFilter,
            hiddenColumns,
          },
        };
      });
    }
    firstRender.current = false;
  }, [debouncedState, setNameChangeApprovalWorkflowTableState]);

  const headers = headerGroups.map(headerGroup => (
    <TableRow {...headerGroup.getHeaderGroupProps()}>
      {headerGroup.headers.map(column => (
        <TableCell
          align={column.align}
          {...column.getHeaderProps({
            style: {
              width: column.width,
              minWidth: column.minWidth,
            },
          })}
        >
          <Box
            component='span'
            sx={sortStyles}
            {...column.getSortByToggleProps({ style: { justifyContent: getHeaderFlexAlignment(column.align) } })}
          >
            {column.render('Header')}
            {column.isSorted ? column.isSortedDesc ? <ArrowDropDown /> : <ArrowDropUp /> : null}
          </Box>
        </TableCell>
      ))}
    </TableRow>
  ));

  const rows = page.length
    ? page.map((row, i) => {
        prepareRow(row);
        const { key, ...rest } = row.getRowProps();
        return <NameChangeReviewTableRow key={key} row={row} {...rest} />;
      })
    : [
        <TableRow key='no-data'>
          <TableCell colSpan={8}>
            <Typography>No name changes have been submitted for review.</Typography>
          </TableCell>
        </TableRow>,
      ];
  return (
    <div>
      <TableContainer sx={containerStyles}>
        <Table size='small' stickyHeader {...getTableProps()}>
          <TableHead>{headers}</TableHead>
          <TableBody {...getTableBodyProps()}>{rows}</TableBody>
        </Table>
      </TableContainer>
      <NameChangeReviewTablePagination instance={instance} totalCount={totalCount} />
    </div>
  );
}

interface NameChangeReviewTableRowProps extends TableRowProps {
  row: Row<NameChangeReviewGridItem>;
}

function NameChangeReviewTableRow({ row, ...props }: NameChangeReviewTableRowProps) {
  const state = useNameChangeReviewListState();

  const highlight = state.id.get() === row.id;
  return (
    <TableRow selected={highlight} {...props}>
      {row.cells.map(cell => (
        <TableCell align={cell.column.align} {...cell.getCellProps()}>
          {cell.render('Cell')}
        </TableCell>
      ))}
    </TableRow>
  );
}
